import { PermissionApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { UserApi } from "lib/api/security/user";
import { IBaseProps } from "lib/data/interfaces";
import { Permission, User } from "lib/data/model";
import { ValidationError } from "lib/data/transfer/ValidationError";
import { StateService, UtilityService } from "lib/services";
import { OmitKeys } from "lib/types/OmitKeys";

import { notification, Button, Form, Input, Spin } from "antd";
import cloneDeep from "lodash/cloneDeep";
import { curry, __ } from "lodash/fp";
import React from "react";

import { PermissionManager } from "./PermissionManager";

interface IRegistrationSectionProps extends IBaseProps {
	user: User;
	hidePermissionManager?: boolean;
	onSaveSuccess?: (user: User, permission?: Permission[]) => void;
	onFormCancel?: () => void;
}

interface IRegistrationSectionState {
	currentUserPermissions: Permission[];
	targetUserPermissions: Permission[];
	userDraft: User;
	isSavingForm: boolean;
}

export class RegistrationSection extends React.Component<IRegistrationSectionProps, IRegistrationSectionState> {

	public static defaultProps = {
		hidePermissionManager: false
	};

	private apiCancellationToken: CancellationToken = new CancellationToken();

	public constructor(props: IRegistrationSectionProps) {

		super(props);

		this.state = {
			currentUserPermissions: [],
			targetUserPermissions: [],
			userDraft: cloneDeep(props.user),
			isSavingForm: false
		};
	}

	public componentWillMount() {

		const currentUserPermissionsPromise = PermissionApi.getUserPermissions();
		let targetUserPermissionsPromise: Promise<Permission[]>;

		// Only query target user permissions if the user already exists
		if (this.props.user.id !== undefined) {

			targetUserPermissionsPromise = PermissionApi.getUserPermissions(this.props.user.id);
		}
		else {

			targetUserPermissionsPromise = Promise.resolve([]);
		}

		Promise
			.all([currentUserPermissionsPromise, targetUserPermissionsPromise])
			.then((responses) => {
				this.setState({
					currentUserPermissions: responses[0],
					targetUserPermissions: responses[1]
				});
			});
	}

	public render() {

		const isEditing = this.props.user.id !== undefined;

		return (
			<div className="registration-wrapper">
				<div>
					<Spin
						spinning={this.state.isSavingForm}
						tip={`${!isEditing ? "Creating" : "Updating"} Account...`}
					>
						<Form className="user-form">

							<Form.Item
								label="Username"
								className="user-form-item"
							>
								<Input
									placeholder="Username"
									name="username"
									onChange={curry(this.onUserInputChange)(__, "username")}
									value={this.state.userDraft.username}
								/>
							</Form.Item>
							<Form.Item
								label="Email"
								className="user-form-item"
							>
								<Input
									placeholder="Email"
									name="email"
									onChange={curry(this.onUserInputChange)(__, "email")}
									value={this.state.userDraft.email}
								/>
							</Form.Item>
							<Form.Item
								label="First Name"
								className="user-form-item"
							>
								<Input
									placeholder="First Name"
									name="firstName"
									onChange={curry(this.onUserInputChange)(__, "firstName")}
									value={this.state.userDraft.firstName}
								/>
							</Form.Item>
							<Form.Item
								label="Last Name"
								className="user-form-item"
							>
								<Input
									placeholder="Last Name"
									name="lastName"
									onChange={curry(this.onUserInputChange)(__, "lastName")}
									value={this.state.userDraft.lastName}
								/>
							</Form.Item>
						</Form>
						{
							!this.props.hidePermissionManager
								?
								(
									<PermissionManager
										onChange={this.setTargetUserPermissions}
										currentUserPermissions={this.state.currentUserPermissions}
										targetUserPermissions={this.state.targetUserPermissions}
									/>
								)
								:
								null
						}
						<div className="button-wrapper">
							<Button
								type="primary"
								onClick={this.createUpdateUser}
							>
								{
									!isEditing ? "Create" : "Update"
								}
							</Button>
							<Button
								icon={!isEditing ? "left" : "close"}
								type="danger"
								onClick={this.props.onFormCancel}
							>
								{
									!isEditing ? "Back to Users" : "Cancel"
								}
							</Button>
						</div>
					</Spin>
				</div>
			</div>
		);
	}

	private setTargetUserPermissions = (permissions: Permission[]) => {

		this.setState({ targetUserPermissions: permissions });
	}

	private onUserInputChange = (event: React.ChangeEvent<HTMLInputElement>, name: keyof OmitKeys<User, ["$type", "id"]>) => {

		this.setState({
			userDraft: Object.assign(this.state.userDraft, { [name]: event.target.value })
		});
	}

	private createUpdateUser = async (event: any) => {

		event.preventDefault();

		StateService.setCancelableState(() => this.setState({
			isSavingForm: true
		}), this.apiCancellationToken);

		const user = await UserApi.updateCreateUser(this.state.userDraft);

		if (!this.props.hidePermissionManager) {

			let didSavePermissions = false;

			if (user !== undefined && user instanceof ValidationError === false) {

				didSavePermissions = await PermissionApi.replaceUserPermissions(user as User, this.state.targetUserPermissions);
			}

			if (didSavePermissions === false) {

				notification.error({
					message: "Couldn't Save Permissions",
					description: "There was an issue while attempting to save permissions"
				});
			}
		}

		if (user instanceof ValidationError || user === undefined) {

			// display the create user form once again
			StateService.setCancelableState(() => this.setState({ isSavingForm: false }), this.apiCancellationToken);

			let formErrorDescription: React.ReactNode = "There was a problem while saving the record";

			if (user !== undefined) {

				const errorCount = user.getErrorList().length;

				formErrorDescription = (
					<>
						<div>
							{
								errorCount > 1
									?
									"There was a problem with at least one of your inputs:"
									:
									"There was a problem with one of the form fields:"
							}
						</div>
						{
							user
								.getErrorList()
								.map((error, index) => (<div key={index}>{errorCount > 1 ? `${index + 1}) ` : ""}{error}</div>))
						}
					</>
				);
			}

			notification.error({
				message: "Form Submission",
				description: formErrorDescription
			});
		}
		else {

			await UtilityService.asyncTimeout(Promise.resolve());
			this.onSuccessfulSave();
		}

		StateService.setCancelableState(() => this.setState({
			isSavingForm: false
		}), this.apiCancellationToken);
	}

	private onSuccessfulSave = () => {

		if (this.props.onSaveSuccess !== undefined) {

			this.props.onSaveSuccess(this.state.userDraft, this.state.targetUserPermissions);
		}
	}
}
